/**
 * A value that settles after the person stops changing it.
 *
 * For inputs that drive a request: without it, a search box fires one call per keystroke, and the
 * answers can arrive out of order — the reply to `mar` landing after the reply to `marina` and
 * overwriting it.
 *
 * @module Hooks
 */

import { useEffect, useState } from 'react';

/**
 * @param value - The value that changes on every keystroke.
 * @param delayMs - How long it must hold still. 300ms is about the gap between words typed at
 * speed, so a fluent typist fires one request per term rather than one per letter.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delayMs);

    // Cleared on every change, so the timer only fires once the value holds still.
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return settled;
}
